import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
const SUBJECTS = [
  "Mathematics",
  "Science",
  "English",
  "Hindi",
];
export default function CurriculumAuthoringTab({ readOnly = false }) {
  const [form, setForm] = useState({
    board: "cbse",
    grade: "6",
    subject: "Mathematics",
    topics: "",
    source_url: "",
    source_version: "",
  });
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadTopics = async () => {
    try {
      const params = new URLSearchParams({
        board: form.board,
        grade: form.grade,
        subject: form.subject,
      });
      const response = await fetch(
        `${API}/admin/curriculum/topics?${params}`,
        { credentials: "include" }
      );
      const data = await response.json();
      if (data.success) {
        setTopics(data.topics || []);
      }
    } catch {
      toast.error("Could not load curriculum drafts");
    }
  };
  useEffect(() => {
    loadTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.board, form.grade, form.subject]);
  const generate = async () => {
    const enteredTopics = form.topics
      .split(/[\n,]+/)
      .map((topic) => topic.trim())
      .filter(Boolean);
    if (!enteredTopics.length) {
      toast.error("Enter at least one topic");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/curriculum/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...form,
          grade: Number(form.grade),
          topics: enteredTopics,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Generation failed");
      }
      toast.success(
        `${data.topics.length} review draft${
          data.topics.length === 1 ? "" : "s"
        } created`
      );
      if (data.failures?.length) {
        toast.error(`${data.failures.length} topic(s) could not be generated`);
      }
      setForm((current) => ({
        ...current,
        topics: "",
      }));
      loadTopics();
    } catch (error) {
      toast.error(error.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };
  const review = async (topic, status) => {
    try {
      const response = await fetch(
        `${API}/admin/curriculum/topics/${topic.id}/review`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ status }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Review failed");
      }
      toast.success(
        status === "approved"
          ? "Curriculum topic published"
          : "Curriculum topic rejected"
      );
      loadTopics();
    } catch (error) {
      toast.error(error.message || "Review failed");
    }
  };
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Curriculum Authoring</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Generate a review draft for one or more topics. Only approved topics
            are available to students.
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <select
              className="h-10 rounded-md border border-input bg-background px-3"
              value={form.board}
              onChange={(event) =>
                setForm({ ...form, board: event.target.value })
              }
            >
              <option value="cbse">CBSE</option>
              <option value="ib">IB</option>
            </select>
            <select
              className="h-10 rounded-md border border-input bg-background px-3"
              value={form.grade}
              onChange={(event) =>
                setForm({ ...form, grade: event.target.value })
              }
            >
              {[5, 6, 7, 8].map((grade) => (
                <option key={grade} value={grade}>
                  Class {grade}
                </option>
              ))}
            </select>
            <select
              className="h-10 rounded-md border border-input bg-background px-3"
              value={form.subject}
              onChange={(event) =>
                setForm({ ...form, subject: event.target.value })
              }
            >
              {SUBJECTS.map((subject) => (
                <option key={subject}>{subject}</option>
              ))}
            </select>
          </div>
          <Textarea
            value={form.topics}
            onChange={(event) =>
              setForm({ ...form, topics: event.target.value })
            }
            placeholder={`Enter one topic per line, for example:
Fractions
Decimals
Ratio and proportion`}
            rows={6}
          />
          <div className="grid gap-3 md:grid-cols-2">
            <Input
              value={form.source_version}
              onChange={(event) =>
                setForm({
                  ...form,
                  source_version: event.target.value,
                })
              }
              placeholder="Source/version, e.g. NCERT 2026–27"
            />
            <Input
              value={form.source_url}
              onChange={(event) =>
                setForm({
                  ...form,
                  source_url: event.target.value,
                })
              }
              placeholder="Official source URL (optional)"
            />
          </div>
          <Button onClick={generate} disabled={readOnly || loading}>
            {loading && (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            )}
            Generate review drafts
          </Button>
        </CardContent>
      </Card>
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">
          Generated curriculum topics
        </h3>
        {topics.length === 0 ? (
          <p className="text-sm text-slate-500">
            No admin-authored topics for this selection yet.
          </p>
        ) : (
          topics.map((topic) => (
            <Card key={topic.id}>
              <CardContent className="flex flex-col gap-3 pt-5 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{topic.topic}</h4>
                    <Badge
                      variant={
                        topic.status === "approved" ? "default" : "secondary"
                      }
                    >
                      {topic.status}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    {topic.concepts?.length || 0} concepts ·{" "}
                    {topic.curriculum_version}
                  </p>
                  <p className="mt-2 text-sm text-slate-700">
                    {topic.concepts?.join(" → ")}
                  </p>
                  {topic.source_version && (
                    <p className="mt-2 text-xs text-slate-500">
                      Source: {topic.source_version}
                    </p>
                  )}
                </div>
                {topic.status === "review" && !readOnly && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => review(topic, "approved")}
                    >
                      <CheckCircle className="mr-1 h-4 w-4" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => review(topic, "rejected")}
                    >
                      <XCircle className="mr-1 h-4 w-4" />
                      Reject
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
